/**
 * ResendCountdown — RTL-aware resend button with countdown timer.
 *
 * Formats countdown with Arabic-Indic numerals + "ث" suffix in RTL,
 * Latin numerals + "s" suffix in LTR.
 *
 * @param cooldown — remaining seconds
 * @param status — resend status (idle | sending | sent)
 * @param onResend — called when user clicks resend
 * @param translate — scoped i18n function (auth namespace)
 */
import { cn, toArabicNumerals } from "@/shared/lib/utils";
import { getEffectiveDir } from "@/shared/lib/direction";

interface ResendCountdownProps {
  cooldown: number;
  status: "idle" | "sending" | "sent";
  onResend: () => void;
  translate: (key: string, fallback?: string) => string;
  disabled?: boolean;
}

const formatCountdown = (s: number, isRTL: boolean) =>
  isRTL ? `${toArabicNumerals(s)}ث` : `${String(s)}s`;

export function ResendCountdown({
  cooldown,
  status,
  onResend,
  translate,
  disabled = false,
}: ResendCountdownProps) {
  const isRTL = getEffectiveDir() === "rtl";

  return (
    <button
      type="button"
      onClick={onResend}
      disabled={disabled || cooldown > 0 || status === "sending"}
      className={cn(
        "text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-150",
        cooldown > 0 || status === "sending"
          ? "cursor-not-allowed"
          : "hover:underline"
      )}
      style={
        cooldown > 0 || status === "sending"
          ? { color: "var(--color-muted-text)" }
          : { color: "var(--color-ornament-dot)" }
      }
    >
      {status === "sent"
        ? translate("verifyEmail.resendSuccess", "Code sent!")
        : cooldown > 0
        ? `${translate("verifyEmail.resendButton", "Resend")} (${formatCountdown(cooldown, isRTL)})`
        : status === "sending"
        ? translate("verifyEmail.resending", "Sending…")
        : translate("verifyEmail.resendButton", "Resend code")}
    </button>
  );
}
